import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_GROW_ANIMATION } from '../chartAnimation';
import { ClrBarHover } from '../chartMarkHover';
import ClrRechartsTooltip from '../ClrRechartsTooltip';
import { buildNiceAxisScale, getSeriesMax } from '../axisScale';
import {
  categoryTickLabel,
  isCategoryPadPayload,
  padCategoriesToBottom,
} from '../padCategoryAxis';

const SERVICE_COLORS = ['#2E8B6F', '#C7872B', '#C0533E', '#26735C', '#8A5E1E', '#A84936'];
const FIRST_GRID_COLOR = '#54555A';
const GRID_COLOR = '#DBDEE4';
const MIN_CATEGORY_SLOTS = 5;

function MostUtilizedServicesCard({ data }) {
  const chartData = useMemo(() => {
    const rows = (data || []).map((r) => ({
      name: r.name || r.service_type || 'Unknown',
      count: r.count ?? r.referral_count ?? 0,
    }));
    return rows.sort((a, b) => b.count - a.count).slice(0, 6);
  }, [data]);

  const plotData = useMemo(
    () => padCategoriesToBottom(chartData, {
      minSlots: MIN_CATEGORY_SLOTS,
      categoryKey: 'name',
      valueKeys: ['count'],
    }),
    [chartData],
  );

  const axis = useMemo(() => {
    const maxCount = getSeriesMax(chartData, ['count']);
    return buildNiceAxisScale(maxCount);
  }, [chartData]);

  const dashedTicks = useMemo(
    () => (axis.ticks || []).filter((tick) => tick > 0),
    [axis.ticks],
  );

  return (
    <div className="clr-chart-card">
      <div className="clr-chart-card-header clr-chart-card-header-accent">
        <h3 className="clr-chart-card-title">Most utilized services</h3>
      </div>
      <div className="clr-chart-card-body">
        {chartData.length === 0 ? (
          <p className="clr-chart-plot clr-chart-empty">No service data</p>
        ) : (
          <div className="clr-chart-plot">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={plotData}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <ReferenceLine x={0} stroke={FIRST_GRID_COLOR} strokeWidth={1} zIndex={100} />
                {dashedTicks.map((tick) => (
                  <ReferenceLine
                    key={`grid-${tick}`}
                    x={tick}
                    stroke={GRID_COLOR}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                    zIndex={100}
                  />
                ))}
                <XAxis
                  type="number"
                  domain={[0, axis.domainMax]}
                  ticks={axis.ticks}
                  height={24}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: '#6b7a74' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={110}
                  tick={{ fontSize: 11, fill: '#3e4f4c' }}
                  tickFormatter={categoryTickLabel}
                  axisLine={false}
                  tickLine={false}
                />
                <ClrRechartsTooltip
                  cursor={{ fill: 'rgba(62, 79, 76, 0.04)' }}
                  formatter={(payload) => {
                    if (isCategoryPadPayload(payload)) return { title: null, rows: [] };
                    const row = payload[0]?.payload;
                    return {
                      title: row?.name || '',
                      rows: [{
                        key: 'count',
                        label: 'Referrals',
                        value: row?.count ?? payload[0]?.value ?? 0,
                      }],
                    };
                  }}
                />
                <Bar
                  dataKey="count"
                  activeBar={ClrBarHover}
                  radius={[0, 0, 0, 0]}
                  barSize={24.756383895874023}
                  maxBarSize={24.756383895874023}
                  {...CHART_GROW_ANIMATION}
                >
                  {plotData.map((entry, idx) => {
                    if (entry.__pad) {
                      return <Cell key={`pad-${idx}`} fill="transparent" />;
                    }
                    const colorIdx = chartData.findIndex((row) => row.name === entry.name);
                    return (
                      <Cell
                        key={entry.name}
                        fill={SERVICE_COLORS[(colorIdx >= 0 ? colorIdx : idx) % SERVICE_COLORS.length]}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

MostUtilizedServicesCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    service_type: PropTypes.string,
    count: PropTypes.number,
    referral_count: PropTypes.number,
  })),
};

export default MostUtilizedServicesCard;
